import {assertAndContinue} from 'Asserts';
import {Collaborator, Owner, OwnerInfo} from 'auth/PermissionConstants';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  createSavedSearch,
  deleteSavedSearch,
  updateSavedSearch,
  UpdateSavedSearchParams,
} from 'search/saved-searches/SavedSearchesDAO';
import {refetchSavedSearchesForSection} from 'search/saved-searches/SavedSearchesQuery';
import {NonPaginatedSearchParams, SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as modals from 'util/Modals';

export async function create({
  name,
  description,
  section,
  params,
  collaboratorsJson,
  ownerJson,
  ownerInfoJson,
}: {
  name: string;
  description: string;
  section: SearchSection;
  params: NonPaginatedSearchParams;
  collaboratorsJson: {collaborators: Array<Collaborator>};
  ownerJson?: Owner;
  ownerInfoJson?: OwnerInfo;
}): Promise<SavedSearch> {
  const response = await createSavedSearch(
    name,
    description,
    section,
    params,
    collaboratorsJson,
    ownerJson,
    ownerInfoJson
  );

  // The Saved Searches in the dropdown are always refreshed, but the ones in the modal will only refresh if the action takes place in the modal.
  refetchSavedSearchesForSection(section);
  return response;
}

export async function update(savedSearchId: string, data: UpdateSavedSearchParams): Promise<SavedSearch> {
  const response = await updateSavedSearch(savedSearchId, data);
  writeFragmentsToCache(getFragmentWrapper('SavedSearch'), [response.savedSearch]);
  return response.savedSearch;
}

export async function confirmAndDelete(
  savedSearch: SavedSearch,
  searchSection: SearchSection
): Promise<boolean> {
  const confirmed = await confirmDelete(savedSearch.name);
  if (!confirmed) {
    return false;
  }
  await deleteSavedSearch(savedSearch.id);

  // The Saved Searches in the dropdown are always refreshed, but the ones in the modal will only refresh if the action takes place in the modal.
  refetchSavedSearchesForSection(searchSection);
  return true;
}

async function confirmDelete(savedSearchName: string | undefined): Promise<boolean> {
  assertAndContinue(Boolean(savedSearchName), 'Expected to have a name defined when deleting a search.');
  // As per the above assert, initialName should always be populated if deleting is enabled, but if
  // we hit a bug, at least give a default value that makes the confirm message readable.
  savedSearchName = savedSearchName || 'Saved search';
  const confirm = await Promise.resolve(
    modals.confirm(
      'Delete saved search?',
      `"${savedSearchName}" will be permanently deleted.`,
      ButtonStyle.NEGATIVE,
      {
        acceptText: 'Delete',
        rejectText: 'Cancel',
        promiseCompatible: true,
        enableRedesign: true,
      }
    )
  );
  return Boolean(confirm);
}
